"use client";

import { useEffect, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth-context";

export default function LoginPage() {
  const router = useRouter();
  const { isAuthenticated, isLoading, login, demoLogin, isDemoMode } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const callbackUrl = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("callbackUrl") || "/dashboard"
    : "/dashboard";

  useEffect(() => {
    if (isAuthenticated) {
      router.replace(callbackUrl);
    }
  }, [isAuthenticated, router, callbackUrl]);

  const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
    if (!credentialResponse.credential) return;
    setError(null);
    try {
      await login(credentialResponse.credential);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed";
      setError(message);
    }
  };

  const handleDemoSignIn = async () => {
    setError(null);
    try {
      await demoLogin();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Demo login failed";
      setError(message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-primary-50/30">
      <div className="w-full max-w-[420px] px-4 animate-scale-in">
        <div className="bg-white rounded-2xl ring-1 ring-slate-200/80 shadow-xl shadow-slate-200/50 p-8">
          <div className="text-center mb-8">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/ghl-logo.jpeg"
              alt="GoHighLevel"
              width={72}
              height={72}
              className="mx-auto mb-4 w-[72px] h-[72px] rounded-2xl shadow-md ring-1 ring-slate-200 object-contain bg-[#021b49] p-1.5"
            />
            <h1 className="text-xl font-bold text-slate-900">Roadmap Radar</h1>
            <p className="text-sm text-slate-500 mt-1">AI-Powered Feature Request Intelligence</p>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-3">
              <div className="w-8 h-8 rounded-full border-2 border-slate-200 border-t-slate-600 animate-spin" />
            </div>
          ) : isDemoMode ? (
            <button
              onClick={() => void handleDemoSignIn()}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white ring-1 ring-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 hover:ring-slate-300 hover:shadow-sm transition-all duration-200"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
              Continue with GHL Mock Sign-In
            </button>
          ) : (
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => console.error("Google sign-in failed")}
                size="large"
                width="350"
                text="signin_with"
                shape="rectangular"
              />
            </div>
          )}

          {error && (
            <div className="mt-5 rounded-xl bg-red-50 ring-1 ring-red-200 px-4 py-3 text-[12px] text-red-800 leading-relaxed">
              {error}
            </div>
          )}

          <p className="text-[11px] text-slate-400 text-center mt-6">
            Restricted to @gohighlevel.com accounts
          </p>

          {isDemoMode && (
            <div className="mt-5 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3 text-[12px] text-amber-800 leading-relaxed">
              Running in demo mode. Sign-in uses a mocked internal GHL user.
            </div>
          )}
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-6">
          Powered by GPT-4o, FAISS, and Canny API
        </p>
      </div>
    </div>
  );
}
